import { cloneDeep } from "lodash";
import { formBuilderGenerateDefaultRow, IFormBuilderRow } from "../../model/formBuilderRow";
import { TFormBuilderItem } from "../../model/item/formBuilderItem";
import { IFormBuilderForm } from "../../model/formBuilderForm";

interface IMoveItemActionToNewRowContext {
  type: "to-new-row";
  prevItemIndex: number;
  nextItemIndex: number;
  prevRowIndex: number;
  nextRowIndex: number;
  prevRowKey: string;
  nextRowKey: string;
  prevRow: IFormBuilderRow;
}

type TMoveItemActionToOtherRowContextOrigin =
{
  type: "do-to-other-row";
  prevRow: IFormBuilderRow;
  prevRowIndex: number;
} |
{
  type: "undo-to-other-row";
  nextRow: IFormBuilderRow;
  nextRowIndex: number;
} |
{
  type: "undo-to-new-row";
  nextRow: IFormBuilderRow;
  nextRowIndex: number;
};

interface IMoveItemActionToOtherRowContext {
  type: "to-other-row";
  origin: TMoveItemActionToOtherRowContextOrigin;
  prevItemIndex: number;
  nextItemIndex: number;
  prevRowKey: string;
  nextRowKey: string;
}

interface IMoveItemActionWithinRowContext {
  type: "within-row";
  prevItemIndex: number;
  nextItemIndex: number;
  rowKey: string;
}

type TMoveItemActionContext =
  | IMoveItemActionToNewRowContext
  | IMoveItemActionToOtherRowContext
  | IMoveItemActionWithinRowContext;

export interface IMoveItemActionPayload {
  key: string;
  context: TMoveItemActionContext;
}

export interface IMoveItemAction {
  type: "MOVE_ITEM";
  payload: IMoveItemActionPayload;
}

export function getMoveItemInverseAction(
  action: IMoveItemAction,
): IMoveItemAction {
  const { key, context } = action.payload;

  switch (context.type) {
    case "to-new-row": {
      return {
        type: "MOVE_ITEM",
        payload: {
          key,
          context: {
            type: "to-other-row",
            origin: {
              type: "undo-to-new-row",
              nextRow: cloneDeep(context.prevRow),
              nextRowIndex: context.prevRowIndex,
            },
            prevItemIndex: context.nextItemIndex,
            nextItemIndex: context.prevItemIndex,
            prevRowKey: context.nextRowKey,
            nextRowKey: context.prevRow.key,
          },
        },
      };
    }
    case "to-other-row": {
      if (context.origin.type !== "do-to-other-row") {
        return null;
      } else {
        return {
          type: "MOVE_ITEM",
          payload: {
            key,
            context: {
              type: "to-other-row",
              origin: {
                type: "undo-to-other-row",
                nextRow: cloneDeep(context.origin.prevRow),
                nextRowIndex: context.origin.prevRowIndex,
              },
              prevItemIndex: context.nextItemIndex,
              nextItemIndex: context.prevItemIndex,
              prevRowKey: context.nextRowKey,
              nextRowKey: context.prevRowKey,
            },
          },
        };
      }
    }
    case "within-row": {
      return {
        type: "MOVE_ITEM",
        payload: {
          key,
          context: {
            type: "within-row",
            prevItemIndex: context.nextItemIndex > context.prevItemIndex ? context.nextItemIndex - 1 : context.nextItemIndex,
            nextItemIndex: context.prevItemIndex > context.nextItemIndex ? context.prevItemIndex + 1 : context.prevItemIndex,
            rowKey: context.rowKey,
          },
        },
      };
    }
  }
}

function moveItem(
  form: IFormBuilderForm,
  key: string,
  context: TMoveItemActionContext,
): IFormBuilderForm {
  if (context.type === "to-new-row") {
    const prevRow = form.children[context.prevRowIndex];
    const item = prevRow?.children[context.prevItemIndex];

    if (prevRow == null || item == null) {
      return form;
    }

    let updatedForm: IFormBuilderForm = form;

    let downShiftOccured = false;

    if (prevRow.children.length <= 1) {
      updatedForm = {
        ...form,
        children: form.children.filter(row => row.key !== prevRow.key),
      };

      if (context.prevRowIndex < context.nextRowIndex) {
        downShiftOccured = true;
      }
    } else {
      updatedForm = {
        ...form,
        children: form.children.map(row => {
          if (row.key === prevRow.key) {
            return {
              ...row,
              children: row.children.filter(child => child.key !== item.key),
            };
          } else {
            return row;
          }
        }),
      };
    }

    const shiftedNextRowIndex = context.nextRowIndex + (downShiftOccured ? -1 : 0);

    const nextRow = formBuilderGenerateDefaultRow([item], context.nextRowKey);

    updatedForm = {
      ...updatedForm,
      children: [
        ...updatedForm.children.slice(0, shiftedNextRowIndex),
        nextRow,
        ...updatedForm.children.slice(shiftedNextRowIndex),
      ],
    };

    return updatedForm;
  } else if (context.type === "to-other-row") {
    if (context.origin.type === "undo-to-new-row") {
      const filteredRows = form.children.filter(row => row.key !== context.prevRowKey);

      const nextRow = context.origin.nextRow;
      const nextRowIndex = context.origin.nextRowIndex;

      if (!form.children.some(row => row.key === nextRow.key)) {
        return {
          ...form,
          children: [
            ...filteredRows.slice(0, nextRowIndex),
            nextRow,
            ...filteredRows.slice(nextRowIndex),
          ],
        };
      } else {
        return {
          ...form,
          children: filteredRows
            .map(row => {
              if (row.key === nextRow.key) {
                return nextRow;
              } else {
                return row;
              }
            }),
        };
      }
    } else if (context.origin.type === "undo-to-other-row") {
      const { nextRow, nextRowIndex } = context.origin;
      
      const updatedForm = {
        ...form,
        children: form.children.map(row => {
          if (row.key === context.prevRowKey) {
            return {
              ...row,
              children: row.children.filter((_, index) => index !== context.prevItemIndex),
            };
          } else {
            return row;
          }
        }),
      };

      if (!form.children.some(row => row.key === nextRow.key)) {
        return {
          ...updatedForm,
          children: [
            ...updatedForm.children.slice(0, nextRowIndex),
            nextRow,
            ...updatedForm.children.slice(nextRowIndex),
          ],
        };
      } else {
        return {
          ...updatedForm,
          children: updatedForm.children.map(row => {
            if (row.key === nextRow.key) {
              return nextRow;
            } else {
              return row;
            }
          }),
        };
      }
    } else if (context.origin.type === "do-to-other-row") {
      const prevRow = form.children.find(row => row.key === context.prevRowKey);

      const prevRowItem = prevRow?.children.find(item => item.key === key);

      if (prevRow == null || prevRowItem == null) {
        return form;
      }

      let updatedForm: IFormBuilderForm = form;

      if (prevRow.children.length <= 1) {
        updatedForm = {
          ...form,
          children: form.children.filter(row => row.key !== prevRow.key),
        };
      } else {
        updatedForm = {
          ...form,
          children: form.children.map(row => {
            if (row.key === prevRow.key) {
              return {
                ...row,
                children: row.children.filter(item => item.key !== prevRowItem.key),
              };
            } else {
              return row;
            }
          }),
        };
      }

      return {
        ...updatedForm,
        children: updatedForm.children.map(row => {
          if (row.key === context.nextRowKey) {
            return {
              ...row,
              children: [
                ...row.children.slice(0, context.nextItemIndex),
                prevRowItem,
                ...row.children.slice(context.nextItemIndex),
              ],
            };
          } else {
            return row;
          }
        }),
      };
    } else {
      return form;
    }
  } else if (context.type === "within-row") {
    const { prevItemIndex, nextItemIndex, rowKey } = context;

    const parentRow = form.children.find(row => row.key === rowKey);

    if (parentRow == null) {
      return form;
    }

    if (
      prevItemIndex < 0 ||
      prevItemIndex >= parentRow.children.length ||
      nextItemIndex < 0 ||
      nextItemIndex > parentRow.children.length ||
      prevItemIndex === nextItemIndex
    ) {
      return form;
    }

    const items: TFormBuilderItem[] = [];

    for (let i = 0; i < parentRow.children.length; i++) {
      if (i === prevItemIndex) {
        continue;
      }
      
      if (i === nextItemIndex) {
        items.push(parentRow.children[prevItemIndex]);
      }
      
      items.push(parentRow.children[i]);
    }

    if (nextItemIndex === parentRow.children.length) {
      items.push(parentRow.children[prevItemIndex]);
    }

    return {
      ...form,
      children: form.children.map(row => {
        if (row === parentRow) {
          return {
            ...row,
            children: items,
          };
        } else {
          return row;
        }
      }),
    };
  } else {
    return form;
  }
}

export function applyMoveItemChange(form: IFormBuilderForm, action: IMoveItemAction): IFormBuilderForm {
  const { key, context } = action.payload;

  return moveItem(form, key, context);
}
